import type { SubscriptionVerdict, UserSubscription } from "../types"

export interface SubscriptionPolicyConfig {
  /** Only subscriptions for this bundle are considered. Empty = any bundle. */
  bundle?: string
  /** Statuses that grant access. Canceled is included by default (access until period end). */
  accessStatuses?: UserSubscription["status"][]
}

/**
 * Pure, framework-free policy that decides whether a user has access based on
 * their subscriptions. Encapsulates the "what counts as active" rules in one place
 * so they can be unit-tested and evolved independently of the UI.
 */
export class SubscriptionPolicy {
  private readonly bundle: string
  private readonly accessStatuses: Set<UserSubscription["status"]>

  constructor(config: SubscriptionPolicyConfig = {}) {
    this.bundle = config.bundle ?? ""
    this.accessStatuses = new Set(config.accessStatuses ?? ["active", "trialing", "canceled"])
  }

  private matchesBundle(sub: UserSubscription): boolean {
    return this.bundle ? sub.bundle === this.bundle : true
  }

  /** Most recent main-plan subscription whose status grants access. */
  findAccessSubscription(subscriptions: UserSubscription[]): UserSubscription | null {
    const candidates = subscriptions
      .filter(
        (sub) => this.matchesBundle(sub) && sub.isMainPlan && this.accessStatuses.has(sub.status),
      )
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())

    return candidates[0] ?? null
  }

  evaluate(subscriptions: UserSubscription[]): SubscriptionVerdict {
    const subscription = this.findAccessSubscription(subscriptions)

    return {
      hasAccess: subscription !== null,
      subscription,
      isCanceled: subscription?.status === "canceled",
    }
  }
}
